import pLimit from 'p-limit';
import { match, constant, isType } from 'variant';
import pWaitFor, { TimeoutError } from 'p-wait-for';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import type { AccountAuth, DeviceCodeResponse } from '../schemas.js';
import {
  client,
  RenewTokenData,
  DeviceCodeNotYetVerifiedError,
} from '../client.js';
import {
  isValidToken,
  deleteAuthData,
  readAccountAuthData,
  saveAuthData,
} from '../auth-data.js';
import type { AuthStrategyState, AuthStrategy } from './auth-strategy-types.js';
import { apiKeyAuthStrategy } from './api-key-auth-strategy.js';

const seconds = (n: number) => 1000 * n;

async function waitForLogin(
  { deviceCode, expiresIn }: DeviceCodeResponse,
  { signal }: { signal: AbortSignal }
) {
  try {
    const authData = await pWaitFor(
      async () => {
        try {
          const tokenData = await client.createToken({
            deviceCode,
          });

          return pWaitFor.resolveWith(tokenData);
        } catch (error) {
          if (error instanceof DeviceCodeNotYetVerifiedError) {
            // continue polling
            return false;
          }

          throw error;
        }
      },
      {
        interval: getTestOverrides().waitForLoginInterval ?? seconds(3),
        timeout: { milliseconds: seconds(expiresIn), signal },
      }
    );

    const userInfo = await client.userInfo({
      accessToken: authData.accessToken,
    });

    const result = { ...authData, userInfo };

    await deleteAuthData();
    await saveAuthData(result);

    return result;
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new CliError({
        code: CliErrorCode.DeviceCodeTimedOut(),
        cause: error,
      });
    }

    throw error;
  }
}

async function renewAccountAuth({ refreshToken, userInfo }: AccountAuth) {
  const renewTokenResult = await client.renewToken({ refreshToken });

  if (isType(renewTokenResult, RenewTokenData.TokenRenewed)) {
    await saveAuthData({ ...renewTokenResult.payload, userInfo });
  }

  return renewTokenResult;
}

async function getNonExpiredAccountAuth(): Promise<AccountAuth | null> {
  const accountAuthStatus = await readAccountAuthData();

  return match(accountAuthStatus, {
    Authenticated: (accountAuth) => accountAuth,
    Unauthenticated: () => null,
    Expired: async (accountAuth) => {
      const renewTokenResult = await renewAccountAuth(accountAuth);

      return match(renewTokenResult, {
        TokenRenewed: ({ payload: newTokenData }) => ({
          ...newTokenData,
          userInfo: accountAuth.userInfo,
        }),
        RefreshTokenRevoked: constant(null),
      });
    },
  });
}

function createAccountAuthState({
  userInfo,
  ...initialTokenData
}: AccountAuth): AuthStrategyState {
  const limiter = pLimit(1);
  let tokenData = { ...initialTokenData };

  return {
    getAccessToken: () => {
      return limiter(async () => {
        if (isValidToken(tokenData)) {
          return tokenData.accessToken;
        }

        const renewTokenResult = await renewAccountAuth({
          ...tokenData,
          userInfo,
        });

        return match(renewTokenResult, {
          TokenRenewed: ({ payload: newTokenData }) => {
            tokenData = newTokenData;
            return tokenData.accessToken;
          },
          RefreshTokenRevoked: ({ cause }) => {
            throw new CliError({
              code: CliErrorCode.FailedToRenewAuthTokenRefreshTokenRevoked(),
              cause,
            });
          },
        });
      });
    },
    getRefreshToken: () => tokenData.refreshToken,
    userInfo,
  };
}

export const accountAuthStrategy: AuthStrategy = {
  async isLoggedIn() {
    const apiKeyAuth = await apiKeyAuthStrategy.isLoggedIn();

    if (apiKeyAuth) {
      return apiKeyAuth;
    }

    const accountAuth = await getNonExpiredAccountAuth();

    if (accountAuth) {
      return createAccountAuthState(accountAuth);
    }

    return null;
  },
  async login({ deviceCode, signal }) {
    const accountAuth = await waitForLogin(deviceCode, { signal });

    return createAccountAuthState(accountAuth);
  },
};
